import {
  CalendarLtrRegular,
  ChatRegular,
  MailRegular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";
import {
  IVaultFile,
  IVaultFolder,
  IVaultSortChoice,
  IVaultWorkflowCard,
  VaultNode,
} from "./VaultPage.types";

export const VAULT_FALLBACK_NAME = "Workspace";

export const VAULT_CONTENT_MAX_WIDTH = "1400px";

export const VAULT_PAGE_SIDE_PADDING = "clamp(16px, 6vw, 80px)";

export const VAULT_SORT_CHOICES: IVaultSortChoice[] = [
  { value: "foldersFirst", label: "Folders first" },
  { value: "name", label: "Name" },
  { value: "recentlyModified", label: "Recently modified" },
];

export const VAULT_MEETINGS_CARD_ID = "meetings";

export const VAULT_WORKFLOW_CARDS: IVaultWorkflowCard[] = [
  {
    id: VAULT_MEETINGS_CARD_ID,
    title: "Meetings",
    description:
      "See your Teams meetings from the last 7 days and save their transcripts to the Vault.",
    buttonLabel: "Open meetings",
    icon: CalendarLtrRegular,
  },
  {
    id: "chats",
    title: "Teams chats",
    description:
      "Pick Teams chats and upload the messages you choose to the Vault.",
    buttonLabel: "Open chats",
    icon: ChatRegular,
  },
  {
    id: "channels",
    title: "Channels",
    description:
      "Browse channel conversations, including replies, and upload selected messages.",
    buttonLabel: "Open channels",
    icon: PeopleTeamRegular,
  },
  {
    id: "emails",
    title: "Emails",
    description: "Find emails by day and save whole threads to the Vault.",
    buttonLabel: "Open emails",
    icon: MailRegular,
  },
];

const PRIMARY_AUTHOR = "Alex Morgan";
const SECONDARY_AUTHOR = "Jordan Lee";

const createDate = (day: number, hours: number, minutes: number): Date =>
  new Date(2026, 9, day, hours, minutes);

const createFile = (
  id: string,
  name: string,
  modifiedAt: Date,
  modifiedBy: string,
): IVaultFile => ({ kind: "file", id, name, modifiedAt, modifiedBy });

const createFolder = (
  id: string,
  name: string,
  modifiedAt: Date,
  modifiedBy: string,
  children: VaultNode[] = [],
): IVaultFolder => ({
  kind: "folder",
  id,
  name,
  modifiedAt,
  modifiedBy,
  children,
});

const createTranscriptFolder = (day: number): IVaultFolder => {
  const dateLabel = `2026-10-${day}`;
  const modifiedAt = createDate(day, 16, 0);
  return createFolder(
    `transcripts/${dateLabel}`,
    dateLabel,
    modifiedAt,
    PRIMARY_AUTHOR,
    [
      createFile(
        `transcripts/${dateLabel}/standup`,
        `${dateLabel}-0800-Platform Daily Standup.md`,
        modifiedAt,
        PRIMARY_AUTHOR,
      ),
    ],
  );
};

export const VAULT_FIXTURE_NODES: VaultNode[] = [
  createFolder("chats", "Chats", createDate(21, 16, 10), PRIMARY_AUTHOR, [
    createFile(
      "chats/daily-standup",
      "Platform Daily Standup (2026-10-21).md",
      createDate(21, 16, 10),
      PRIMARY_AUTHOR,
    ),
    createFile(
      "chats/vault-channel",
      "Vault channel (2026-10-21).md",
      createDate(21, 16, 10),
      PRIMARY_AUTHOR,
    ),
  ]),
  createFolder("emails", "Emails", createDate(22, 15, 41), SECONDARY_AUTHOR, [
    createFile(
      "emails/deployment-status",
      "2026-10-22 Deployment Status.md",
      createDate(22, 15, 41),
      SECONDARY_AUTHOR,
    ),
  ]),
  createFolder(
    "meeting-inbox",
    "Meeting Inbox",
    createDate(21, 16, 11),
    PRIMARY_AUTHOR,
  ),
  createFolder("meetings", "Meetings", createDate(21, 16, 10), PRIMARY_AUTHOR, [
    createFile(
      "meetings/standup-20",
      "Platform Daily Standup (2026-10-20).md",
      createDate(20, 16, 0),
      PRIMARY_AUTHOR,
    ),
    createFile(
      "meetings/standup-21",
      "Platform Daily Standup (2026-10-21).md",
      createDate(21, 16, 0),
      PRIMARY_AUTHOR,
    ),
    createFile(
      "meetings/planning-sync",
      "Q3 Planning Sync (2026-10-19).md",
      createDate(19, 17, 30),
      PRIMARY_AUTHOR,
    ),
  ]),
  createFolder(
    "my-project",
    "My Project",
    createDate(21, 16, 11),
    PRIMARY_AUTHOR,
  ),
  createFolder(
    "summaries",
    "Summaries",
    createDate(21, 16, 11),
    PRIMARY_AUTHOR,
  ),
  createFolder(
    "transcripts",
    "Transcripts",
    createDate(21, 16, 10),
    PRIMARY_AUTHOR,
    [
      createTranscriptFolder(20),
      createTranscriptFolder(21),
      createTranscriptFolder(22),
    ],
  ),
  createFile(
    "loop-paragraph",
    "Loop paragraph.loop",
    createDate(22, 15, 47),
    SECONDARY_AUTHOR,
  ),
];
